import { useMemo, useState } from 'react'
import { EmptyState, LoadingState } from '@orbind/ui'
import { rotuloDoMes, type ArquivoDoAcervo, type LinkDoAcervo } from '@orbind/core'
import { aoClicarLinkExterno } from '../../lib/link-externo.ts'
import { urlDoAnexo, useAcervo } from './useChat.ts'

/**
 * Mídia, links e docs — a aba maior do painel de dados (pedido 2026-09-14,
 * referência: o mensageiro). Três abas (Mídia, Documentos, Links), cada uma
 * agrupada por mês ("Neste mês", "Mês passado", "Agosto de 2026"); mídia em
 * grade de quadrados, documentos e links em lista. O rodapé troca o recorte
 * entre ESTA conversa e todas as conversas.
 */

type Aba = 'midia' | 'documentos' | 'links'

const ABAS: { id: Aba; rotulo: string }[] = [
  { id: 'midia', rotulo: 'Mídia' },
  { id: 'documentos', rotulo: 'Documentos' },
  { id: 'links', rotulo: 'Links' },
]

export function AcervoDeMidia({
  channelId,
  onVoltar,
  onIrParaMensagem,
}: {
  channelId: string
  onVoltar: () => void
  onIrParaMensagem: (channelId: string, messageId: string) => void
}) {
  const [aba, setAba] = useState<Aba>('midia')
  const [todas, setTodas] = useState(false)
  const acervo = useAcervo(todas ? null : channelId)

  const itens = acervo.data?.[aba] ?? []
  const grupos = useMemo(() => agruparPorMes<ArquivoDoAcervo | LinkDoAcervo>(itens), [itens])

  return (
    <div className="ch-acervo">
      <div className="ch-drawer__head">
        <button type="button" className="ch-drawer__back" aria-label="Voltar aos dados" onClick={onVoltar}>
          <i className="fas fa-arrow-left" aria-hidden />
        </button>
        <span className="ch-drawer__id">
          <span className="ch-drawer__title">Mídia, links e docs</span>
          <span className="ch-drawer__sub">{todas ? 'De todas as conversas' : 'Desta conversa'}</span>
        </span>
      </div>

      <div className="ch-acervo__abas" role="tablist" aria-label="Tipo de conteúdo">
        {ABAS.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={aba === a.id}
            className={`ch-acervo__aba${aba === a.id ? ' ch-acervo__aba--on' : ''}`}
            onClick={() => setAba(a.id)}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      <div className="ch-acervo__corpo" role="tabpanel">
        {acervo.isPending ? (
          <div style={{ padding: 16 }}>
            <LoadingState rows={4} label="Carregando" />
          </div>
        ) : acervo.isError && !acervo.data ? (
          <div style={{ padding: '28px 16px' }}>
            <EmptyState
              icon="fas fa-exclamation-triangle"
              title="Não foi possível carregar"
              description="Verifique a conexão e tente de novo."
            />
          </div>
        ) : itens.length === 0 ? (
          <div style={{ padding: '28px 16px' }}>
            <EmptyState
              icon={aba === 'midia' ? 'fas fa-images' : aba === 'documentos' ? 'fas fa-file-alt' : 'fas fa-link'}
              title={
                aba === 'midia'
                  ? 'Nenhuma foto ou vídeo'
                  : aba === 'documentos'
                  ? 'Nenhum documento'
                  : 'Nenhum link'
              }
              description={
                todas ? 'Nada deste tipo foi compartilhado nas suas conversas.' : 'Nada deste tipo foi compartilhado nesta conversa.'
              }
            />
          </div>
        ) : (
          grupos.map(([mes, doMes]) => (
            <section key={mes} className="ch-acervo__mes">
              <span className="ch-acervo__rotulo">{mes}</span>
              {aba === 'midia' ? (
                <div className="ch-acervo__grade">
                  {(doMes as ArquivoDoAcervo[]).map((item) => (
                    <Miniatura key={item.id} item={item} mostrarConversa={todas} />
                  ))}
                </div>
              ) : aba === 'documentos' ? (
                (doMes as ArquivoDoAcervo[]).map((item) => (
                  <a
                    key={item.id}
                    className="ch-contact ch-acervo__linha"
                    href={urlDoAnexo(item.id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="ch-contact__avatar">
                      <i className={item.tipo === 'audio' ? 'fas fa-headphones' : iconeDoArquivo(item)} aria-hidden />
                    </span>
                    <span className="ch-contact__main">
                      <span className="ch-contact__name">{item.fileName ?? 'Arquivo'}</span>
                      <span className="ch-contact__sub">
                        {[tamanho(item.sizeBytes), dataCurta(item.enviadaEm), todas ? item.canal : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </a>
                ))
              ) : (
                (doMes as LinkDoAcervo[]).map((link) => (
                  <div key={link.id} className="ch-contact ch-acervo__linha">
                    <span className="ch-contact__avatar">
                      <i className="fas fa-link" aria-hidden />
                    </span>
                    <span className="ch-contact__main">
                      {/* no desktop, o mesmo desvio dos links do balão: navegador padrão */}
                      <a
                        className="ch-acervo__url"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={aoClicarLinkExterno}
                        onAuxClick={aoClicarLinkExterno}
                      >
                        {link.dominio}
                      </a>
                      <span className="ch-contact__sub ch-acervo__trecho">{link.trecho}</span>
                      <span className="ch-contact__sub">
                        {[dataCurta(link.enviadaEm), todas ? link.canal : null].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="ch-iconbtn"
                      aria-label="Ir para a mensagem"
                      title="Ir para a mensagem"
                      onClick={() => onIrParaMensagem(link.channelId, link.messageId)}
                    >
                      <i className="fas fa-comment-dots" aria-hidden />
                    </button>
                  </div>
                ))
              )}
            </section>
          ))
        )}
      </div>

      <button type="button" className="ch-acervo__rodape" onClick={() => setTodas((v) => !v)}>
        <i className={todas ? 'fas fa-comment' : 'fas fa-photo-video'} aria-hidden />
        {todas ? 'Ver só desta conversa' : 'Ver mídias de todas as conversas'}
      </button>
    </div>
  )
}

/** Quadrado da grade: foto, quadro do vídeo ou GIF; abre o original. */
export function Miniatura({ item, mostrarConversa = false }: { item: ArquivoDoAcervo; mostrarConversa?: boolean }) {
  const url = item.urlExterna ?? urlDoAnexo(item.id)
  const video = item.tipo === 'video' || (item.mimeType ?? '').startsWith('video/')
  return (
    <a
      className="ch-acervo__mini"
      href={url}
      target="_blank"
      rel="noreferrer"
      title={[item.fileName, mostrarConversa ? item.canal : null].filter(Boolean).join(' · ') || undefined}
    >
      {video ? (
        <>
          {/* `#t=0.1` faz o navegador mostrar um quadro em vez de fundo preto. */}
          <video src={`${url}#t=0.1`} preload="metadata" muted playsInline />
          <span className="ch-acervo__play" aria-hidden>
            <i className="fas fa-play" />
          </span>
        </>
      ) : (
        <img src={url} alt={item.fileName ?? ''} loading="lazy" />
      )}
      {item.tipo === 'gif' ? <span className="ch-acervo__selo">GIF</span> : null}
    </a>
  )
}

function agruparPorMes<T extends { enviadaEm: string }>(itens: readonly T[]): [string, T[]][] {
  const grupos = new Map<string, T[]>()
  for (const item of itens) {
    const mes = rotuloDoMes(item.enviadaEm)
    const lista = grupos.get(mes) ?? []
    lista.push(item)
    grupos.set(mes, lista)
  }
  return [...grupos]
}

function iconeDoArquivo(item: ArquivoDoAcervo): string {
  const nome = (item.fileName ?? '').toLowerCase()
  if (nome.endsWith('.pdf')) return 'fas fa-file-pdf'
  if (/\.(xlsx?|csv)$/.test(nome)) return 'fas fa-file-excel'
  if (/\.(docx?|odt)$/.test(nome)) return 'fas fa-file-word'
  if (/\.(pptx?)$/.test(nome)) return 'fas fa-file-powerpoint'
  if (/\.(zip|rar|7z)$/.test(nome)) return 'fas fa-file-archive'
  return 'fas fa-file-alt'
}

function tamanho(bytes: number | null): string | null {
  if (!bytes) return null
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}

function dataCurta(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
